import type { AccessToken, TokenProvider } from '@taler/api-client'
import type { TokenStore } from './token-store.ts'

/** Obtains a token without user interaction. Null when only a sign-in can produce one. */
export type Renew = () => Promise<AccessToken | null>

export interface TokenProviderOptions {
  readonly store: TokenStore
  readonly renew: Renew
}

/**
 * The token seam. The client asks for a token and, on a rejection, for a fresh one; how a
 * fresh one is obtained is the sign-in flow's business and lives behind `renew`. Concurrent
 * callers share one renewal, so a page that fires five queries does not open five of them.
 */
export function createTokenProvider(options: TokenProviderOptions): TokenProvider {
  let inFlight: Promise<AccessToken | null> | null = null

  const renewOnce = async (): Promise<AccessToken | null> => {
    inFlight ??= options
      .renew()
      .then((token) => {
        if (token !== null) options.store.write(token)
        return token
      })
      .finally(() => {
        inFlight = null
      })
    return inFlight
  }

  return {
    async current() {
      return options.store.readUsable() ?? (await renewOnce())
    },
    refresh: renewOnce,
    signOut: () => options.store.clear(),
  }
}
